import { api } from '@/lib/api';
import type { FileIntakeActionMode, FileIntakePurpose } from './file-intake-dsl';

export interface FileEntityLinkInput {
  entityType: string;
  entityId: string;
  entityRef?: string;
  linkType?: 'primary' | 'related' | 'evidence' | 'backup';
}

export interface FileUploadContext {
  entityType?: string;
  entityId?: string;
  source?: 'upload' | 'email' | 'whatsapp' | 'system' | 'ocr' | 'api';
  sourceChannel?: 'gmail' | 'outlook' | 'whatsapp' | 'web_upload' | 'api' | 'system' | 'go_pwa';
  sourceRef?: string;
  docType?: string;
  displayName?: string | null;
  description?: string;
  tags?: string[];
  autoAnalyze?: boolean;
  metadata?: Record<string, unknown>;
  entityLinks?: FileEntityLinkInput[];
}

export interface UploadedFileRecord {
  fileId: string;
  fileUrl: string;
  fileName: string;
  mimeType: string;
  fileSize?: number | null;
}

export interface FilePurposeDeclarationInput {
  purpose: FileIntakePurpose;
  instruction?: string | null;
  site?: string | null;
  category?: string | null;
  autoActionMode?: FileIntakeActionMode;
  autoIndexKnowledge?: boolean;
  sourceSurface?: string | null;
  sourceChannel?: 'gmail' | 'outlook' | 'whatsapp' | 'web_upload' | 'api' | 'system' | 'go_pwa' | null;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return btoa(
    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
  );
}

export async function uploadFileToFileShare(
  file: File,
  context: FileUploadContext = {},
): Promise<UploadedFileRecord> {
  const buffer = await file.arrayBuffer();
  const base64 = arrayBufferToBase64(buffer);

  const result = await api.post<{
    id: string;
    fileUrl: string;
    file: {
      id: string;
      file_url: string;
      file_name: string | null;
      mime_type: string | null;
      file_size: number | null;
    };
  }>('/fileshare/upload', {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    data: base64,
    entityType: context.entityType,
    entityId: context.entityId,
    source: context.source ?? 'upload',
    sourceChannel: context.sourceChannel ?? 'web_upload',
    sourceRef: context.sourceRef ?? null,
    docType: context.docType,
    displayName: context.displayName ?? null,
    description: context.description,
    tags: context.tags,
    autoAnalyze: context.autoAnalyze ?? false,
    metadata: context.metadata,
    entityLinks: context.entityLinks,
  });

  return {
    fileId: result.id ?? result.file.id,
    fileUrl: result.fileUrl ?? result.file.file_url,
    fileName: result.file.file_name ?? file.name,
    mimeType: result.file.mime_type ?? (file.type || 'application/octet-stream'),
    fileSize: result.file.file_size ?? file.size,
  };
}

export async function uploadFilesToFileShare(
  files: File[],
  context: FileUploadContext = {},
): Promise<UploadedFileRecord[]> {
  return Promise.all(files.map((file) => uploadFileToFileShare(file, context)));
}

export async function declareFilePurpose(
  fileId: string,
  input: FilePurposeDeclarationInput,
): Promise<{
  file: Record<string, unknown>;
  intake: Record<string, unknown>;
  createdDraft?: Record<string, unknown> | null;
}> {
  return api.post(`/fileshare/files/${fileId}/intake`, {
    purpose: input.purpose,
    instruction: input.instruction ?? null,
    site: input.site ?? null,
    category: input.category ?? null,
    autoActionMode: input.autoActionMode ?? 'suggest',
    autoIndexKnowledge: input.autoIndexKnowledge ?? false,
    sourceSurface: input.sourceSurface ?? null,
    sourceChannel: input.sourceChannel ?? null,
    targetEntityType: input.targetEntityType ?? null,
    targetEntityId: input.targetEntityId ?? null,
  });
}
